import { useState, useEffect, useCallback, useMemo } from 'react'
import { Loader2, RefreshCw, Server, AlertTriangle, Activity, Wifi } from 'lucide-react'
import { getSystemObservability, type SystemObservabilitySnapshot } from '../../../lib/api'
import { useAutoRefresh } from '../../../hooks/useAutoRefresh'
import { AnalyticsNav } from './AnalyticsOverview'
import StatCard from '../../../components/analytics/StatCard'
import TrendChart from '../../../components/analytics/TrendChart'
import { CHART_PALETTE } from '../../../lib/chart-colors'

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, exp)
  return `${value.toFixed(exp === 0 ? 0 : 1)} ${units[exp]}`
}

function formatTimestamp(value?: string): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

export default function SystemObservability() {
  const [snapshot, setSnapshot] = useState<SystemObservabilitySnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [includeStopped, setIncludeStopped] = useState(false)
  const [kindFilter, setKindFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [query, setQuery] = useState('')

  const loadData = useCallback(async () => {
    try {
      const data = await getSystemObservability({
        include_stopped: includeStopped,
        limit: 400,
        history_points: 180,
      })
      setSnapshot(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load observability data')
      throw err
    } finally {
      setLoading(false)
    }
  }, [includeStopped])

  useEffect(() => {
    setLoading(true)
    loadData()
  }, [loadData])

  useAutoRefresh(loadData, {
    baseInterval: 30000,
    maxInterval: 120000,
  })

  const chartData = useMemo(() => {
    if (!snapshot) return []
    let prevFailures = 0
    let prevClosed = 0
    let prevHttpTransportErrors = 0
    let prevHttpTimeoutErrors = 0
    return snapshot.history.map((point) => {
      const failureDelta = Math.max(0, point.websocket_connect_failures_total - prevFailures)
      const closedDelta = Math.max(0, point.websocket_closed_connections - prevClosed)
      const httpTransportErrorDelta = Math.max(0, point.http_proxy_transport_errors_total - prevHttpTransportErrors)
      const httpTimeoutErrorDelta = Math.max(0, point.http_proxy_timeout_errors_total - prevHttpTimeoutErrors)
      prevFailures = point.websocket_connect_failures_total
      prevClosed = point.websocket_closed_connections
      prevHttpTransportErrors = point.http_proxy_transport_errors_total
      prevHttpTimeoutErrors = point.http_proxy_timeout_errors_total
      return {
        date: new Date(point.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        running_containers: point.running_containers,
        unhealthy_containers: point.unhealthy_containers,
        cpu_percent_sum: point.cpu_percent_sum,
        memory_percent: point.memory_percent,
        websocket_active: point.websocket_active,
        websocket_failures_delta: failureDelta,
        websocket_closed_delta: closedDelta,
        http_proxy_p50_latency_ms: point.http_proxy_p50_latency_ms,
        http_proxy_p95_latency_ms: point.http_proxy_p95_latency_ms,
        http_proxy_transport_errors_delta: httpTransportErrorDelta,
        http_proxy_timeout_errors_delta: httpTimeoutErrorDelta,
      }
    })
  }, [snapshot])

  const rows = useMemo(() => {
    if (!snapshot) return []
    const q = query.trim().toLowerCase()
    return snapshot.containers.items.filter((item) => {
      if (kindFilter !== 'all' && item.kind !== kindFilter) return false
      if (statusFilter !== 'all' && item.status !== statusFilter) return false
      if (!q) return true
      const haystack = [
        item.name,
        item.kind,
        item.status,
        item.labels.app_name ?? '',
        item.labels.user_email ?? '',
        item.labels.deployment ?? '',
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [snapshot, kindFilter, statusFilter, query])

  const wsRecentEvents = useMemo(() => {
    if (!snapshot) return []
    return [...snapshot.websocket.recent_events].reverse().slice(0, 30)
  }, [snapshot])

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">System Observability</h1>
          <p className="text-muted-foreground">Live websocket + container telemetry across the platform</p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-secondary transition-colors text-sm disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <AnalyticsNav />

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {loading && !snapshot ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : !snapshot ? (
        <div className="text-center py-12 text-muted-foreground">No observability data available</div>
      ) : (
        <>
          <div className="mb-3 text-xs text-muted-foreground">
            Snapshot: {formatTimestamp(snapshot.generated_at)}
            {' · '}
            Cache age: {snapshot.collection.cache_age_seconds.toFixed(1)}s
            {' · '}
            Last refresh: {snapshot.collection.last_refresh_ms.toFixed(0)}ms
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-10 gap-4 mb-8">
            <StatCard label="Containers (Total)" value={snapshot.containers.total} icon={Server} />
            <StatCard label="Running" value={snapshot.containers.running} icon={Server} />
            <StatCard label="Unhealthy" value={snapshot.containers.unhealthy} icon={AlertTriangle} />
            <StatCard label="WS Active" value={snapshot.websocket.active_connections} icon={Wifi} />
            <StatCard label="WS Connect Failures" value={snapshot.system_health.websocket_connect_failures_total} icon={Activity} />
            <StatCard label="HTTP p95 (ms)" value={snapshot.http_proxy.latency_ms.p95} icon={Activity} />
            <StatCard label="Proxy Timeouts" value={snapshot.system_health.http_proxy_timeout_errors_total} icon={AlertTriangle} />
            <StatCard label="Proxy Transport Errors" value={snapshot.system_health.http_proxy_transport_errors_total} icon={AlertTriangle} />
            <StatCard
              label="Stats Sampled"
              value={`${snapshot.containers.resource_collection.sampled_running_containers}/${snapshot.containers.running}`}
              icon={Server}
            />
            <StatCard label="Cache TTL (s)" value={snapshot.collection.cache_ttl_seconds} icon={Activity} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="text-lg font-semibold mb-4">Websocket Trend</h3>
              <TrendChart
                data={chartData}
                lines={[
                  { dataKey: 'websocket_active', label: 'WS Active', color: CHART_PALETTE[0] },
                  { dataKey: 'websocket_failures_delta', label: 'WS Failures/Sample', color: CHART_PALETTE[3] },
                  { dataKey: 'websocket_closed_delta', label: 'WS Closes/Sample', color: CHART_PALETTE[2] },
                ]}
                height={280}
              />
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="text-lg font-semibold mb-4">Resource Trend</h3>
              <TrendChart
                data={chartData}
                lines={[
                  { dataKey: 'running_containers', label: 'Running Containers', color: CHART_PALETTE[1] },
                  { dataKey: 'memory_percent', label: 'Memory %', color: CHART_PALETTE[4] },
                  { dataKey: 'cpu_percent_sum', label: 'CPU % Sum', color: CHART_PALETTE[5] },
                ]}
                height={280}
              />
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="text-lg font-semibold mb-4">Responsiveness Trend</h3>
              <TrendChart
                data={chartData}
                lines={[
                  { dataKey: 'http_proxy_p50_latency_ms', label: 'HTTP p50 (ms)', color: CHART_PALETTE[5] },
                  { dataKey: 'http_proxy_p95_latency_ms', label: 'HTTP p95 (ms)', color: CHART_PALETTE[4] },
                  { dataKey: 'http_proxy_timeout_errors_delta', label: 'Timeouts/Sample', color: CHART_PALETTE[3] },
                  { dataKey: 'http_proxy_transport_errors_delta', label: 'Transport Errors/Sample', color: CHART_PALETTE[2] },
                ]}
                height={280}
              />
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 mb-4">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeStopped}
                  onChange={(e) => setIncludeStopped(e.target.checked)}
                  className="rounded border-border"
                />
                Include stopped
              </label>
              <select
                value={kindFilter}
                onChange={(e) => setKindFilter(e.target.value)}
                className="px-3 py-1.5 rounded border border-border bg-background text-sm"
              >
                <option value="all">All kinds</option>
                <option value="workspace">Workspace</option>
                <option value="app">App</option>
                <option value="platform">Platform</option>
                <option value="other">Other</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 rounded border border-border bg-background text-sm"
              >
                <option value="all">All statuses</option>
                {Object.keys(snapshot.containers.status_counts).map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by container/app/user..."
                className="px-3 py-1.5 rounded border border-border bg-background text-sm min-w-[240px]"
              />
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              Stats collection: sampled {snapshot.containers.resource_collection.sampled_running_containers}, failed {snapshot.containers.resource_collection.failed_running_containers}, skipped {snapshot.containers.resource_collection.skipped_running_containers}, budget {snapshot.containers.resource_collection.stats_budget_seconds}s
              {' · '}
              cache age {snapshot.collection.cache_age_seconds.toFixed(1)}s / ttl {snapshot.collection.cache_ttl_seconds.toFixed(0)}s
              {snapshot.collection.last_refresh_error
                ? ` · last refresh error: ${snapshot.collection.last_refresh_error}`
                : ''}
            </div>
          </div>

          <div className="rounded-xl border border-border overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-secondary">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium">Container</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Kind</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">CPU</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Memory</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Network</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Labels</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((item) => (
                  <tr key={item.id} className="hover:bg-secondary/50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{item.name}</div>
                      <div className="text-xs text-muted-foreground">Started: {formatTimestamp(item.started_at)}</div>
                    </td>
                    <td className="px-4 py-3 capitalize">{item.kind}</td>
                    <td className="px-4 py-3">
                      <div className="capitalize">{item.status}</div>
                      <div className={`text-xs ${item.health === 'unhealthy' ? 'text-red-500' : 'text-muted-foreground'}`}>
                        health: {item.health}
                      </div>
                    </td>
                    <td className="px-4 py-3">{item.metrics_sampled ? `${item.cpu_percent.toFixed(2)}%` : '—'}</td>
                    <td className="px-4 py-3">
                      <div>{item.metrics_sampled ? `${item.memory_percent.toFixed(2)}%` : '—'}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.metrics_sampled
                          ? `${formatBytes(item.memory_usage_bytes)} / ${formatBytes(item.memory_limit_bytes)}`
                          : '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs">RX: {item.metrics_sampled ? formatBytes(item.network_rx_bytes) : '—'}</div>
                      <div className="text-xs">TX: {item.metrics_sampled ? formatBytes(item.network_tx_bytes) : '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      <div>app: {item.labels.app_name || '—'}</div>
                      <div>user: {item.labels.user_email || '—'}</div>
                      <div>deploy: {item.labels.deployment || '—'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 mt-6">
            <h3 className="text-lg font-semibold mb-3">Recent WebSocket Events</h3>
            {wsRecentEvents.length === 0 ? (
              <div className="text-sm text-muted-foreground">No websocket events captured since router start.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead className="bg-secondary">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium">Time</th>
                      <th className="text-left px-3 py-2 text-xs font-medium">Event</th>
                      <th className="text-left px-3 py-2 text-xs font-medium">Path</th>
                      <th className="text-left px-3 py-2 text-xs font-medium">Initiator/Reason</th>
                      <th className="text-left px-3 py-2 text-xs font-medium">Codes</th>
                      <th className="text-left px-3 py-2 text-xs font-medium">Target</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {wsRecentEvents.map((event, idx) => {
                      const eventRecord = event as Record<string, unknown>
                      const timestamp = typeof eventRecord.timestamp === 'string' ? eventRecord.timestamp : ''
                      const kind = typeof eventRecord.event === 'string' ? eventRecord.event : 'unknown'
                      const path = typeof eventRecord.path === 'string' ? eventRecord.path : '—'
                      const initiator = typeof eventRecord.initiator === 'string' ? eventRecord.initiator : ''
                      const reason = typeof eventRecord.reason === 'string' ? eventRecord.reason : ''
                      const clientCode = eventRecord.client_close_code
                      const backendCode = eventRecord.backend_close_code
                      const host = typeof eventRecord.target_host === 'string' ? eventRecord.target_host : '—'
                      const port = eventRecord.target_port
                      const clientCodeText = (typeof clientCode === 'number' || typeof clientCode === 'string')
                        ? String(clientCode)
                        : '—'
                      const backendCodeText = (typeof backendCode === 'number' || typeof backendCode === 'string')
                        ? String(backendCode)
                        : '—'
                      return (
                        <tr key={`${timestamp}-${kind}-${idx}`} className="text-xs">
                          <td className="px-3 py-2">{formatTimestamp(timestamp)}</td>
                          <td className="px-3 py-2">{kind}</td>
                          <td className="px-3 py-2">{path}</td>
                          <td className="px-3 py-2">{initiator || reason || '—'}</td>
                          <td className="px-3 py-2">
                            c:{clientCodeText} / b:{backendCodeText}
                          </td>
                          <td className="px-3 py-2">{host}:{String(port ?? '—')}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
